// The admin section (only David's account): small pure helpers, tested in tests/admin.test.mjs.
// Whether someone is admin is decided by the server on every admin call; isAdmin() only decides what the app shows.

/** Texas Hold'em in the admin view: a player's hand now and with the whole board, and the winning hand (AdminGameStateBuilder.TexasHoldem). */
export const POKER_HAND_DETAILS = ["HAND_NOW", "HAND_RIVER", "WINNING_HAND"];

/** The badges beside a player's name: who leads the Hold'em hand now, who wins it or splits it at the river. */
export const BADGE_STYLE = Object.freeze({ LEADS: "text-bg-warning", WINS: "text-bg-primary", SPLITS: "text-bg-secondary" });

/** GET /user says IsAdmin for the admin account (Config.ADMIN_USER_IDS on the server). */
export function isAdmin(user) {
    return !!(user && user.IsAdmin === true);
}

/**
 * The top bar's shield in an open round, for the admin account only: straight to the round in the admin section
 * (David 2026-10-20). Null elsewhere and for everyone else.
 */
export function adminRoundLink(user, routeName, roundId) {
    if (!isAdmin(user) || routeName !== "GamePlay" || !roundId)
        return null;
    return { name: "AdminGame", params: { gameid: String(roundId) } };
}

/** The games list's status filter: the server's StatusGroup values ("" = all). */
export const GAME_STATUS_FILTERS = ["", "ongoing", "waiting", "finished"];

/** Users whose name, e-mail or phone contains the text (any case). */
export function filterUsers(users, text) {
    const list = Array.isArray(users) ? users : [];
    const needle = (text || "").trim().toLowerCase();
    if (!needle)
        return list;
    return list.filter(u => [`${u.FirstName || ""} ${u.LastName || ""}`, u.Email, u.Phone]
        .some(value => (value || "").toLowerCase().includes(needle)));
}

/** "Linn Carlberg" */
export function fullName(user) {
    return `${(user && user.FirstName) || ""} ${(user && user.LastName) || ""}`.trim();
}

/** Query string for GET /admin/games; empty filters are left out. */
export function gamesQuery({ cardGameId, state, player, offset, limit } = {}) {
    const params = new URLSearchParams();
    if (cardGameId) params.set("CardGameId", String(cardGameId));
    if (state && GAME_STATUS_FILTERS.includes(state)) params.set("State", state);
    if (player && player.trim()) params.set("Player", player.trim());
    if (offset) params.set("Offset", String(offset));
    if (limit) params.set("Limit", String(limit));
    const query = params.toString();
    return query ? `?${query}` : "";
}

/** The same short device name as the server's push devices: "iPhone (Safari)". */
export function deviceSummary(userAgent) {
    const ua = userAgent || "";
    const has = (text) => ua.toLowerCase().includes(text.toLowerCase());
    const device = has("iPhone") ? "iPhone" : has("iPad") ? "iPad" : has("Android") ? "Android"
        : has("Macintosh") ? "Mac" : has("Windows") ? "Windows" : has("Linux") ? "Linux" : "Okänd enhet";
    const browser = has("Edg/") ? "Edge" : has("Firefox/") || has("FxiOS/") ? "Firefox"
        : has("Chrome/") || has("CriOS/") ? "Chrome" : has("Safari/") ? "Safari" : null;
    return browser ? `${device} (${browser})` : device;
}

const CARD_KEY = /^(10|[2-9JQKA])[SHDC]$/;

/** A key there is a card picture for (anything else is shown as text, never require()d). */
export function isCardKey(key) {
    return typeof key === "string" && CARD_KEY.test(key);
}

/** null when the number of chosen players fits the game type, else "few" or "many". */
export function playerCountProblem(count, min, max) {
    if (min && count < min) return "few";
    if (max && count > max) return "many";
    return null;
}

/** The entries in a list of addresses (commas, semicolons, spaces, new lines), as the server splits them. */
export function splitEmails(text) {
    return (text || "").split(/[\s,;]+/).map(x => x.trim()).filter(x => x.length > 0);
}

/** The stored game state, indented; the text as it is when it is not JSON. */
export function prettyJson(text) {
    if (!text)
        return "";
    try {
        return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
        return text;
    }
}

/** A new list with the id added or removed (never changes the list it gets). */
export function toggleId(list, id) {
    const current = Array.isArray(list) ? list : [];
    return current.includes(id) ? current.filter(x => x !== id) : [...current, id];
}

/** Players for a new game: the chosen owner, else the first chosen. */
export function ownerOf(playerIds, ownerId) {
    const ids = Array.isArray(playerIds) ? playerIds : [];
    return ids.includes(ownerId) ? ownerId : (ids[0] ?? null);
}

/** How long an admin call waits before its second try. */
export const ADMIN_RETRY_DELAY_MS = 1000;

/**
 * Whether an admin call gets a second try: a read that got no answer (status 0) or a 5xx, e.g. when the account's
 * Lambda concurrency is full while the app starts. Only reads: a POST may have gone through (a news notification to
 * everyone must not go out twice).
 */
export function shouldRetryAdmin(method, status) {
    return method === "GET" && (status === 0 || status >= 500);
}

/** Translation keys for values from the server. */
export const keys = {
    userStatus: (status) => `Admin_User_Status_${(status && status.Text) || "Unknown"}`,
    gameStatus: (group) => `Admin_Game_Status_${group || "all"}`,
    group: (key) => `Admin_Group_${key}`,
    flag: (flag) => `Admin_Flag_${flag}`,
    badge: (badge) => `Admin_Badge_${badge}`,
    detail: (key) => `Admin_Detail_${key}`,
    playerState: (state) => `Admin_Player_State_${(state && state.Text) || "Unknown"}`,
    gameState: (state) => `Admin_Game_State_${(state && state.Text) || "Unknown"}`,
    // a Hold'em hand now and at the river is a poker hand's name, translated as on the table (Poker_Hand_Pair)
    value: (key, value) => POKER_HAND_DETAILS.includes(key) ? `Poker_Hand_${value}` : `Admin_Value_${key}_${value}`,
    invitation: (result) => `Admin_Invite_Result_${result}`
};

/**
 * A player's card groups as the page shows them: numbered groups of one kind in a row (Skitgubbe's visible piles 1-3)
 * become one group on one row, each card marked with its pile under it, as the hidden cards are marked (David
 * 2026-10-10: a row each took too much room). The groups from the server are not changed.
 */
export function playerCardGroups(groups) {
    const shown = [];
    for (const group of groups || []) {
        const last = shown[shown.length - 1];
        const cards = (group.Cards || []).map(card => (group.Label ? { ...card, Pile: group.Label } : card));
        if (group.Label && last && last.Piles && last.Key === group.Key)
            last.Cards.push(...cards);
        else
            shown.push(group.Label ? { ...group, Label: null, Piles: true, Cards: cards } : group);
    }
    return shown;
}
